import { For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "solid-transition-group";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-solid";

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const [toasts, setToasts] = createStore<Toast[]>([]);

export function showToast(message: string, type: ToastType = "info") {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type }]);

  setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== id));
  }, TOAST_DURATION);
}

export function Toaster() {
  const removeToast = (id: string) => {
    setToasts(toasts.filter((t) => t.id !== id));
  };

  return (
    <Portal>
      <div
        class="fixed right-4 z-[300] flex flex-col gap-2 w-full max-w-[400px]"
        style={{ bottom: "calc(1rem + var(--safe-area-inset-bottom))" }}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>
            {(toast) => (
              <div
                class="flex items-center justify-between gap-3 p-4 rounded-lg border shadow-lg bg-card"
                classList={{
                  "border-success/30 bg-success/5 text-success": toast.type === "success",
                  "border-destructive/30 bg-destructive/5 text-destructive": toast.type === "error",
                  "border-primary/30 bg-primary/5 text-primary": toast.type === "info",
                }}
              >
                <div class="flex items-center gap-3">
                  <Show when={toast.type === "success"}>
                    <CheckCircle2 class="h-5 w-5" />
                  </Show>
                  <Show when={toast.type === "error"}>
                    <AlertCircle class="h-5 w-5" />
                  </Show>
                  <Show when={toast.type === "info"}>
                    <Info class="h-5 w-5" />
                  </Show>
                  <span class="text-sm font-medium">{toast.message}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    removeToast(toast.id);
                  }}
                  class="p-1 hover:bg-black/5 rounded-full transition-colors"
                >
                  <X class="h-4 w-4" />
                </button>
              </div>
            )}
          </For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
